import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  AuthContext,
  LayoutContext,
  TeamContext,
  TeamContextProvider,
} from 'contexts';
import { useBreakpoint } from 'hooks';

import { UserRoles } from '@bb/users';
import { Redirect } from 'react-router-dom';
import AppTitle from './AppTitle';
import MyBusinessPageHeader from './MyBusinessPageHeader';
import MyBusinessPageContent from './MyBusinessPageContent';

export type DashboardType = 'me' | 'team';

const MyBusinessPageWrapper: React.FC = () => {
  const { auth } = useContext(AuthContext);

  return (
    <TeamContextProvider auth={auth}>
      <MyBusinessPage />
    </TeamContextProvider>
  );
};

const MyBusinessPage: React.FC = () => {
  const { md } = useBreakpoint();
  const { auth, hasFeature } = useContext(AuthContext);
  const { setAppTitle } = useContext(LayoutContext);
  const { targetTeamState } = useContext(TeamContext);
  const [dashboardType, setDashboardType] = useState<DashboardType>('me');
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  useEffect(() => {
    if (!auth) return;

    const isTeamAdmin = auth.role === UserRoles['Team Admin'];

    if (isTeamAdmin && auth.isLicensed) {
      setAppTitle(
        <AppTitle
          dashboardType={dashboardType}
          currentTeamName={targetTeamState.name}
          openDropdown={setIsMobileDropdownOpen}
        />,
      );
      return;
    }

    const defaultDashboardType: DashboardType =
      isTeamAdmin && !auth.isLicensed && hasFeature('TEAMS') ? 'team' : 'me';

    setAppTitle(
      <AppTitle
        dashboardType={defaultDashboardType}
        currentTeamName={targetTeamState.name}
      />,
    );
  }, [auth, dashboardType, hasFeature, setAppTitle, targetTeamState.name]);

  if (!auth) return null;

  const isTeamAdmin = auth.role === UserRoles['Team Admin'];

  if (auth.isLicensed && isTeamAdmin && hasFeature('TEAMS')) {
    return (
      <Container noPadding={!md}>
        <MyBusinessPageHeader
          dashboardType={dashboardType}
          setDashboardType={setDashboardType}
        />
        <MyBusinessPageContent
          dashboardType={dashboardType}
          isMobileDropdownOpen={isMobileDropdownOpen}
          setDashboardType={setDashboardType}
          setIsMobileDropdownOpen={setIsMobileDropdownOpen}
        />
      </Container>
    );
  }

  if (isTeamAdmin && !auth.isLicensed && !hasFeature('TEAMS')) {
    return <Redirect to="/feature-missing" />;
  }

  const defaultDashboardType: DashboardType = isTeamAdmin ? 'team' : 'me';

  return (
    <Container noPadding={!md}>
      <MyBusinessPageHeader dashboardType={defaultDashboardType} />
      <MyBusinessPageContent
        dashboardType={defaultDashboardType}
        isMobileDropdownOpen={isMobileDropdownOpen}
        setIsMobileDropdownOpen={setIsMobileDropdownOpen}
      />
    </Container>
  );
};

export default MyBusinessPageWrapper;

const Container = styled.div<{ noPadding: boolean }>`
  width: 100%;
  height: 100%;
  padding: ${({ noPadding }) => (noPadding ? 0 : '32px')};
`;
